/* Workspace — AI Research tasks
 *
 *  Endpoints:
 *    POST   /api/research                       { question, force_deep? } → { task_id }
 *    GET    /api/tasks                          → { tasks: [...] }
 *    GET    /api/tasks/:id                      → task with result_markdown
 *    GET    /api/tasks/:id/progress             → live progress
 *    POST   /api/tasks/:id/cancel
 *    DELETE /api/tasks/:id
 */

const STATUS_META = {
  running:   { label: '進行中', tone: 'warn',    dot: true },
  completed: { label: '已完成', tone: 'accent',  dot: true },
  failed:    { label: '失敗',   tone: 'down',    dot: true },
  cancelled: { label: '已取消', tone: 'neutral', dot: true },
};

/* Expressive 外殼專屬：手機把任務清單／詳情兩欄改成上下堆疊。No-op under Bento. */
GxCore.injectCss('gx-css-p-research', String.raw`
@media (max-width:899px){
html[data-ui-shell="expressive"] .gx-research-split{grid-template-columns:minmax(0,1fr)!important}
}
`);

function ResearchPage() {
  const toast = useToast();
  const [tasks, setTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);

  const refresh = React.useCallback(() => {
    setLoading(true);
    api('GET', '/tasks')
      .then(d => setTasks(d.tasks || []))
      .catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  React.useEffect(() => { refresh(); }, []);

  // Auto-refresh while any task is running
  React.useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'running');
    if (!hasRunning) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [tasks, refresh]);

  const runningCount = tasks.filter(t => t.status === 'running').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const failedCount = tasks.filter(t => t.status === 'failed').length;

  return (
    <>
      <PageHeader
        eyebrow="工作空間 · AI"
        title="AI 研究"
        italic="Deep Research"
        action={
          <Button variant="primary" icon="plus" size="lg" onClick={() => setShowNew(true)}>
            新研究任務
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="rwd-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { eyebrow: '進行中', value: runningCount, color: HR.warn },
          { eyebrow: '已完成', value: completedCount, color: HR.accent },
          { eyebrow: '失敗', value: failedCount, color: HR.down },
          { eyebrow: '總任務', value: tasks.length, color: HR.ink },
        ].map((s, i) => (
          <div key={i} style={uiStyle({
            padding: '14px 18px', background: HR.surface,
            border: `1px solid ${HR.rule}`, borderRadius: 6,
          }, { padding: '16px 18px', background: GxT.color.card, border: 'none', borderRadius: GxT.radius.card })}>
            <div style={uiStyle({
              fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase',
            }, { fontSize: 13, fontWeight: 700, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none' })}>{s.eyebrow}</div>
            <div style={uiStyle({
              fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 500, marginTop: 4,
              color: s.value > 0 ? s.color : HR.inkFaint,
            }, {
              fontWeight: GxT.weight.display, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
              // 語意色維持（44px 大字，AA 只需 3:1）；0 值改 onV（faint 已足 AA，但與 Bento「灰」語意一致且更穩）
              color: s.value > 0 ? s.color : GxT.color.onV,
            })}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className={GxCore.cls(undefined, 'gx-research-split')} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 18 }}>
        {/* Task list */}
        <Card
          padding="0"
          eyebrow="Tasks"
          title="我的任務"
          italic={`${tasks.length} 個`}
          right={<Button size="sm" variant="ghost" icon="refresh" onClick={refresh}>重新整理</Button>}
        >
          {loading ? (
            <SkeletonTable rows={4} cols={['1fr', 90, 80]} header={false} />
          ) : tasks.length === 0 ? (
            <EmptyState
              icon="search"
              title="尚無研究任務"
              hint="點上方「新研究任務」開始問 AI 一個問題"
              action={<Button variant="primary" icon="plus" onClick={() => setShowNew(true)}>新研究任務</Button>}
            />
          ) : (
            <div style={{ maxHeight: 560, overflowY: 'auto' }}>
              {tasks.map(t => {
                const meta = STATUS_META[t.status] || STATUS_META.running;
                const active = t.id === selectedId;
                return (
                  <div
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    style={uiStyle({
                      padding: '14px 18px', cursor: 'pointer',
                      borderTop: `1px solid ${HR.ruleSoft}`,
                      background: active ? HR.surfaceAlt : 'transparent',
                      borderLeft: active ? `3px solid ${HR.accent}` : '3px solid transparent',
                    }, { padding: '14px 20px 14px 17px', borderTop: `1px solid ${GxT.color.s3}`, background: active ? GxT.color.s2 : 'transparent' })}
                  >
                    <div style={uiStyle({
                      fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 500,
                      marginBottom: 6, color: HR.ink,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }, { fontSize: 15, fontWeight: active ? 700 : 600 })}>
                      {t.question}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, fontSize: FontSizes.trading_lg, color: HR.inkFaint,
                    }}>
                      <span style={{ fontFamily: HR.mono }}>#{t.id} · {fmtDateTime(t.created_at)}</span>
                      <Pill tone={meta.tone} dot={meta.dot}>{meta.label}</Pill>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Detail panel */}
        <div>
          {selectedId ? (
            <TaskDetail
              taskId={selectedId}
              onChanged={refresh}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <Card eyebrow="Detail" title="任務詳情" italic="Select a task">
              <div style={uiStyle({ padding: '40px 20px', textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs },
                { color: GxT.color.onV, fontSize: 14 })}>
                從左邊選一個任務檢視結果
              </div>
            </Card>
          )}
        </div>
      </div>

      {showNew && <NewResearchModal onCancel={() => setShowNew(false)} onCreated={(id) => {
        setShowNew(false);
        refresh();
        setSelectedId(id);
      }} />}
    </>
  );
}

function TaskDetail({ taskId, onChanged, onClose }) {
  const toast = useToast();
  const [task, setTask] = React.useState(null);
  const [progress, setProgress] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    api('GET', `/tasks/${taskId}`)
      .then(t => setTask(t))
      .catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [taskId, toast]);

  React.useEffect(() => { setLoading(true); setProgress(null); load(); }, [taskId]);

  // Poll progress while running
  React.useEffect(() => {
    if (!task || task.status !== 'running') return;
    let alive = true;
    const tick = () => {
      api('GET', `/tasks/${taskId}/progress`)
        .then(p => { if (alive && p.active) setProgress(p); else if (alive) { load(); onChanged && onChanged(); } })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [task, taskId, load, onChanged]);

  if (loading) return <Card eyebrow="Detail" title="—"><SkeletonText lines={5} /></Card>;
  if (!task) return null;

  const meta = STATUS_META[task.status] || STATUS_META.running;
  const isRunning = task.status === 'running';

  return (
    <Card
      padding="22px 24px"
      eyebrow={`Task #${task.id}`}
      title="任務詳情"
      italic={fmtDateTime(task.created_at)}
      right={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Pill tone={meta.tone} dot={meta.dot}>{meta.label}</Pill>
          {isRunning && (
            <Button size="sm" variant="ghost" onClick={async () => {
              if (!confirm('取消此研究任務？')) return;
              try {
                await api('POST', `/tasks/${task.id}/cancel`);
                toast('已取消', 'info');
                load(); onChanged && onChanged();
              } catch (e) { toast(e.detail || '失敗', 'error'); }
            }} style={{ color: HR.warn }}>取消</Button>
          )}
          {!isRunning && (
            <Button size="sm" variant="ghost" onClick={async () => {
              if (!confirm(`刪除任務 #${task.id}？`)) return;
              try {
                await api('DELETE', `/tasks/${task.id}`);
                toast('已刪除', 'ok');
                onClose && onClose();
                onChanged && onChanged();
              } catch (e) { toast(e.detail || '失敗', 'error'); }
            }} style={{ color: HR.down }}>刪除</Button>
          )}
        </div>
      }
    >
      <div style={uiStyle({
        fontFamily: HR.serif, fontSize: FontSizes.trading_md, color: HR.ink, marginBottom: 16,
        padding: '12px 14px', background: HR.surfaceAlt, borderRadius: 4,
        borderLeft: `3px solid ${HR.accent}`, lineHeight: 1.6,
      }, { fontSize: 15, fontWeight: 600, color: GxT.color.onPc, padding: '14px 16px', background: GxT.color.pc, borderRadius: GxT.radius.inner, borderLeft: 'none' })}>
        {task.question}
      </div>

      {/* Stats row */}
      <div style={uiStyle({
        display: 'flex', gap: 16, marginBottom: 16,
        fontSize: FontSizes.trading_xl, color: HR.inkFaint, fontFamily: HR.mono,
      }, { flexWrap: 'wrap', gap: '6px 16px', color: GxT.color.onV })}>
        <span>Tokens: <strong style={{ color: HR.ink }}>{(task.tokens_used || progress?.tokens_used || 0).toLocaleString()}</strong></span>
        <span>耗時: <strong style={{ color: HR.ink }}>{task.elapsed_seconds || progress?.elapsed_seconds || 0}s</strong></span>
        {task.completed_at && <span>完成於: <strong style={{ color: HR.ink }}>{fmtDateTime(task.completed_at)}</strong></span>}
      </div>

      {/* Running: live progress */}
      {isRunning && progress && (
        <div style={uiStyle({
          padding: 14, background: '#FAEFD8',
          border: `1px solid ${GxCore.alpha(HR.warn, '33')}`, borderLeft: `3px solid ${HR.warn}`,
          borderRadius: 4, marginBottom: 16,
        }, { padding: '14px 16px', background: GxT.tint.warn.bg, color: GxT.tint.warn.fg, border: 'none', borderLeft: 'none', borderRadius: GxT.radius.inner })}>
          <div style={uiStyle({ fontSize: FontSizes.trading_lg, color: HR.warn, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 },
            { fontSize: 13, fontWeight: 800, color: GxT.tint.warn.fg, letterSpacing: 0, textTransform: 'none' })}>
            進行中 · {progress.elapsed_seconds}s
          </div>
          <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 500, marginBottom: 8 }, { fontWeight: 700 })}>
            {progress.current_step || '處理中…'}
          </div>
          {progress.steps && progress.steps.length > 0 && (
            <div style={uiStyle({ fontSize: FontSizes.trading_xl, color: HR.inkSoft, fontFamily: HR.mono, lineHeight: 1.6 }, { color: GxT.tint.warn.fg })}>
              {progress.steps.slice(-5).map((s, i) => (
                <div key={i}>· {s}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {task.error_message && (
        <div style={uiStyle({
          padding: 14, background: '#F4DAD8',
          border: `1px solid ${GxCore.alpha(HR.down, '33')}`, borderRadius: 4, marginBottom: 16,
          fontSize: FontSizes.trading_md, color: HR.down, fontFamily: HR.mono,
        }, { padding: '14px 16px', background: GxT.tint.down.bg, color: GxT.tint.down.fg, border: 'none', borderRadius: GxT.radius.inner })}>
          ⚠ {task.error_message}
        </div>
      )}

      {/* Result markdown */}
      {task.result_markdown && (
        <div>
          <div style={uiStyle({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 },
            { fontSize: 13, fontWeight: 800, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none' })}>
            報告 · Result
          </div>
          <pre style={uiStyle({
            margin: 0, padding: '16px 18px',
            background: HR.surfaceAlt, borderRadius: 4,
            fontFamily: HR.serif, fontSize: FontSizes.trading_md, lineHeight: 1.7, color: HR.ink,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 520, overflowY: 'auto',
            border: `1px solid ${HR.rule}`,
          }, { background: GxT.color.s1, borderRadius: GxT.radius.inner, fontSize: 14, border: 'none' })}>
{task.result_markdown}
          </pre>
        </div>
      )}
    </Card>
  );
}

function NewResearchModal({ onCancel, onCreated }) {
  const toast = useToast();
  const [question, setQuestion] = React.useState('');
  const [deep, setDeep] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!question.trim()) { toast('請輸入問題', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await api('POST', '/research', {
        question: question.trim(),
        force_deep: deep,
      });
      toast(`任務 #${r.task_id} 已建立`, 'ok');
      onCreated(r.task_id);
    } catch (e) { toast(e.detail || '建立失敗', 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal open onClose={onCancel} title="新增研究任務" subtitle="New Research Task" size="md"
      footer={<>
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
        <Button variant="primary" onClick={submit} disabled={submitting}>
          {submitting ? '建立中…' : '開始研究'}
        </Button>
      </>}>
      <Field label="研究問題" required hint="清楚、具體的問題會得到更好的報告">
        <Textarea value={question} onChange={setQuestion} rows={5} placeholder="例：分析台積電 2026 Q1 的營收動能，比較同業..." />
      </Field>
      <div style={{ marginTop: 14 }}>
        <Checkbox checked={deep} onChange={setDeep}
          label="強制 Deep mode（更深入但耗時更長 / 較多 token）" />
      </div>
    </Modal>
  );
}

window.ResearchPage = ResearchPage;
